import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
// eslint-disable-next-line import/named
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import map from 'lodash/map';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { useGradebookSorting } from '../../hooks/useGradebookSorting';
import { useGradebookSettings } from '../../hooks/useGradebookSettings';
import { useGradebookData } from '../../hooks/useGradebookData';
import { useGradebookColumns } from '../../hooks/useGradebookColumns';
import { CategoryHeader } from './CategoryHeader';
import { AssignmentHeader } from './AssignmentHeader';
import { AverageRow } from './AverageRow';
import { TableBody } from './TableBody';
import { formatGrade, findRowIndexById } from './utils';
import {
  getToolIds,
  shouldHideSidePanel,
} from '@macmillan-learning/gradebook-shared';
import {
  toggleCategoryExpandCollapse,
  openIndividualStudentGradebook,
  getGradebook,
  getGradebookInstructor,
  updateGradebook,
} from '../../../../../actions/gradebook';
import useSidePanel from '../../../../SidePanel/hooks/useSidePanel';
import { SET_PANEL_PARAMS } from '../../../../SidePanel/store/actions';
import { getConfigObject } from '../../../../../utils/config';
import { createIndividualStudentGradebookUrl } from '../../../../../utils/InstructorGradebookHelpers';
import NoSearchResultsGradebook from '../../../Components/NoSearchResultsGradebook';
import EmptyGradebook from '../../../Components/EmptyGradebook';
import { SEARCH_TYPES } from '../../../../../utils/constants';
import {
  STICKY_COLUMN_WIDTH,
  ROW_HEIGHT,
  VIRTUALIZER_OVERSCAN,
  STICKY_HEADER_HEIGHT,
  SCROLL_PADDING,
  AVERAGES_FETCH_DEBOUNCE,
  COLUMN_WIDTHS,
  ROW_INDEX,
} from './constants';
import styles from './Gradebook.module.css';

function Gradebook({
  data,
  calculations,
  onAnnouncement,
  showPercent,
  collapsedCategoryIds,
  courseId,
  dispatch,
  pageCount: _reduxPageCount,
  getNextGradebookPage: _getNextGradebookPage,
  showGradeOverrideDialog,
  hasInstructorLinkedCourse: _hasInstructorLinkedCourse,
  rdxGradebookSettings,
  sortColumn,
  sortDirection,
  hasStudentReport,
  getStudentReportLink,
  getLaunchUrl,
  averages,
  updateAssignmentAverages,
  page: reduxPage,
  pageSize,
  count,
  getGradebook: _getGradebookAction,
  getGradebookInstructor: _getGradebookInstructorProp,
  searchPhrase,
  searchType,
  onTabChange,
  isGradebookReFetching,
  disableManualAssignmentsMenu,
  exceptionGroups,
  filterTypes,
}) {
  // Get side panel dispatch for opening side panel on grade click
  const { dispatch: sidePanelDispatch } = useSidePanel();
  const {
    FF_SIDE_PANEL_ACTIVE = false,
    FF_HIDE_GUIDED_READING_RESPONSES = false,
  } = getConfigObject();

  // Extract settings from Redux state
  const { roundPercentType, gradingType, requireStudentId } =
    useGradebookSettings(rdxGradebookSettings);

  // Use Redux collapsed categories if provided, otherwise local state
  const [localCollapsedCategories, setLocalCollapsedCategories] = useState(
    new Set(),
  );
  const collapsedCategories = useMemo(() => {
    if (collapsedCategoryIds && collapsedCategoryIds.length > 0) {
      return new Set(collapsedCategoryIds.map(String));
    }
    return localCollapsedCategories;
  }, [collapsedCategoryIds, localCollapsedCategories]);

  const [focusedCell, setFocusedCell] = useState(null);
  const [focusedStudentId, setFocusedStudentId] = useState(null);

  // Initialize pageIndex from Redux page (0-indexed for TanStack Table)
  const [pagination, setPagination] = useState({
    pageIndex: (reduxPage || 1) - 1,
    pageSize,
  });

  // Keep TanStack pagination pageSize in sync with session state
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageSize,
    }));
  }, [pageSize]);

  // Initialize sorting hook with Redux dependencies
  // Use imported action creators (not props) to avoid double-dispatch issue
  const { handleSort, getColumnSortState, getAriaSortValue } =
    useGradebookSorting({
      dispatch,
      getGradebook: (cid, opts) => getGradebook(cid, opts),
      getGradebookInstructor: (cid, opts) => getGradebookInstructor(cid, opts),
      updateGradebook: (opts) => updateGradebook(opts),
      courseId,
      reduxSortColumn: sortColumn,
      reduxSortDirection: sortDirection,
      count,
      limit: pageSize,
      searchPhrase,
      exceptionGroups,
      categories: data.categories,
      showPercent,
    });

  const toggleCategory = useCallback(
    (categoryId) => {
      // Track that we need to restore focus after the re-render
      pendingCategoryFocusRef.current = categoryId;

      // If dispatch is available, use Redux action
      if (dispatch) {
        dispatch(toggleCategoryExpandCollapse(categoryId));
      } else {
        // Fallback to local state
        setLocalCollapsedCategories((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(String(categoryId))) {
            newSet.delete(String(categoryId));
          } else {
            newSet.add(String(categoryId));
          }
          return newSet;
        });
      }
    },
    [dispatch],
  );

  // Handle grade cell click - matches V1's behavior
  // Opens side panel when FF_SIDE_PANEL_ACTIVE is true (unless tool hides side panel)
  // Opens grade override dialog when side panel is not active
  const handleGradeClick = useCallback(
    (gradeData) => {
      const tool = gradeData.tool;
      const shouldSidePanelBeActive =
        FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(tool);

      // If side panel is active for this tool, open the side panel instead of the dialog
      if (shouldSidePanelBeActive) {
        const { assessment } = getToolIds();
        const isAssessment = tool === assessment;
        const isgUrl = createIndividualStudentGradebookUrl({
          studentId: gradeData.userId,
          showPercent,
        });

        // Build selector to restore focus when panel closes
        const activeEl = document.activeElement;
        const previousFocusSelector =
          activeEl?.dataset?.rowIndex !== undefined &&
          activeEl?.dataset?.columnId
            ? `[data-row-index="${activeEl.dataset.rowIndex}"][data-column-id="${activeEl.dataset.columnId}"]`
            : null;

        const shouldHideResponses =
          FF_HIDE_GUIDED_READING_RESPONSES && gradeData.subtype === 'cca';

        sidePanelDispatch({
          type: SET_PANEL_PARAMS,
          payload: {
            resourceId: gradeData.assignmentId,
            studentId: gradeData.userId,
            hasResponses:
              isAssessment && gradeData.hasLaunched && !shouldHideResponses,
            type: 'student',
            actualCourseId: gradeData.course_id,
            isgUrl,
            courseId,
            open: true,
            previousFocusSelector,
          },
        });
        return;
      }

      // Side panel not active - open grade override dialog (V1 fallback behavior)
      if (!showGradeOverrideDialog) return;

      // Generate studentReportLink if getStudentReportLink is available
      let studentReportLink = null;
      if (getStudentReportLink && gradeData.userId && gradeData.assignmentId) {
        studentReportLink = getStudentReportLink(
          gradeData.userId,
          gradeData.assignmentId,
        );
      }

      // Generate studentAssignmentLink for assessment/goreact tools that have been launched
      let studentAssignmentLink = null;
      const hasLaunched = gradeData.hasLaunched;
      const { assessment, goreact } = getToolIds();
      const supportsUserAssignmentLinks =
        tool === assessment || tool === goreact;

      if (getLaunchUrl && supportsUserAssignmentLinks && hasLaunched) {
        const activityLink = getLaunchUrl({
          ...gradeData,
          id: gradeData.assignmentId,
          actual_course_id: gradeData.course_id,
        });
        if (activityLink) {
          studentAssignmentLink = `${activityLink}&studentUserId=${gradeData.userId}`;
        }
      }

      showGradeOverrideDialog({
        ...gradeData,
        hasStudentReport,
        studentReportLink,
        studentAssignmentLink,
      });
    },
    [
      FF_SIDE_PANEL_ACTIVE,
      showPercent,
      courseId,
      sidePanelDispatch,
      showGradeOverrideDialog,
      hasStudentReport,
      getStudentReportLink,
      getLaunchUrl,
    ],
  );

  // Handle assignment header click - opens side panel for assignment view
  // Matches V1 behavior in GridCell.jsx where clicking assignment name opens side panel
  const handleAssignmentHeaderClick = useCallback(
    (assignmentData) => {
      const tool = assignmentData.tool;
      const shouldSidePanelBeActive =
        FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(tool);

      if (shouldSidePanelBeActive) {
        // Build selector to restore focus when panel closes
        const activeEl = document.activeElement;
        const previousFocusSelector =
          activeEl?.dataset?.rowIndex !== undefined &&
          activeEl?.dataset?.columnId
            ? `[data-row-index="${activeEl.dataset.rowIndex}"][data-column-id="${activeEl.dataset.columnId}"]`
            : null;

        sidePanelDispatch({
          type: SET_PANEL_PARAMS,
          payload: {
            resourceId: assignmentData.id,
            studentId: '', // empty for assignment view (not student-specific)
            hasResponses: false,
            type: 'assignment',
            actualCourseId: assignmentData.course_id,
            isgUrl: '',
            courseId,
            open: true,
            previousFocusSelector,
          },
        });
      }
    },
    [sidePanelDispatch, courseId, FF_SIDE_PANEL_ACTIVE],
  );

  // Handle student name click to open individual gradebook
  const handleStudentClick = useCallback(
    (studentId) => {
      if (dispatch) {
        dispatch(openIndividualStudentGradebook(studentId));
      }
    },
    [dispatch],
  );

  // Process data for the table using extracted hook
  const { rows, averageRow, categoryGroups } = useGradebookData({
    data,
    calculations,
    averages,
  });

  // Build columns using extracted hook
  const columns = useGradebookColumns({
    data,
    categoryGroups,
    collapsedCategories,
    getColumnSortState,
    handleSort,
    showPercent,
    handleGradeClick,
    handleStudentClick,
    handleAssignmentHeaderClick,
    showGradeOverrideDialog,
    roundPercentType,
    rdxGradebookSettings,
    requireStudentId,
    disableManualAssignmentsMenu,
    gradingType,
  });

  // Keep average row separate from student rows for sticky positioning
  const tableData = useMemo(() => rows, [rows]);

  const table = useReactTable({
    data: tableData,
    columns,
    // Default row ids are just array position, which shifts on re-sort;
    // key by student id so a focused/selected row can be re-located after
    // the row order changes.
    getRowId: (row) => String(row.student.id),
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    manualPagination: true,
    rowCount: data.totalStudents,
    state: {
      pagination,
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    meta: {
      collapsedCategories,
      toggleCategory,
    },
    autoResetAll: false,
  });

  const { rows: tableRows } = table.getRowModel();
  const parentRef = useRef(null);
  const tableRef = useRef(null);

  // Row virtualizer
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: VIRTUALIZER_OVERSCAN.ROWS,
  });

  // Handle Enter/Space on data cells - clicks the button inside the cell
  const handleCellEdit = useCallback(({ rowIndex, columnId }) => {
    const cell = document.querySelector(
      `[data-row-index="${rowIndex}"][data-column-id="${columnId}"]`,
    );
    const button = cell?.querySelector('button');
    if (button) {
      button.click();
    }
  }, []);

  // Ref to track pending focus restoration after category toggle
  const pendingCategoryFocusRef = useRef(null);

  // Handle Enter/Space activation for header cells.
  // Cells with multiple interactive elements are blocked by the generic
  // multi-element check in useKeyboardNavigation — those require Tab-in.
  const handleCellActivate = useCallback(
    ({ rowIndex, columnId }) => {
      const column = columns.find((c) => c.id === columnId);

      if (rowIndex === ROW_INDEX.CATEGORY) {
        // Category row: toggle category collapse
        const categoryId = column?.meta?.category;
        if (categoryId) {
          toggleCategory(categoryId);
        }
      }
    },
    [columns, toggleCategory],
  );

  // Initialize keyboard navigation - pass scrollToRow to handle virtualized rows
  const { focusedCell: kbFocusedCell, setFocusedCell: setKbFocusedCell } =
    useKeyboardNavigation({
      tableRows,
      columns,
      onCellEdit: handleCellEdit,
      onCellActivate: handleCellActivate,
      onAnnouncement,
      data,
      scrollToRow: (rowIndex) => rowVirtualizer.scrollToIndex(rowIndex),
      gradingType,
      showPercent,
    });

  // Restore focus after category collapse/expand
  useEffect(() => {
    if (pendingCategoryFocusRef.current) {
      const categoryId = pendingCategoryFocusRef.current;
      pendingCategoryFocusRef.current = null;

      // Poll for a cell in this category to appear after React re-renders
      // Use data-category-id to find any visible cell in the category
      // (the specific column might be scrolled out of view due to virtualization)
      let attempts = 0;
      const maxAttempts = 10;

      const tryFocus = () => {
        attempts++;
        // Find any cell in the category header row that belongs to this category
        const cellElement = document.querySelector(
          `[data-row-index="${ROW_INDEX.CATEGORY}"][data-category-id="${categoryId}"]`,
        );
        if (cellElement instanceof HTMLElement) {
          cellElement.focus();
          // Update keyboard navigation state to match
          const columnId = cellElement.dataset.columnId;
          if (columnId) {
            setKbFocusedCell({
              rowIndex: ROW_INDEX.CATEGORY,
              columnId,
            });
          }
        } else if (attempts < maxAttempts) {
          requestAnimationFrame(tryFocus);
        }
      };

      requestAnimationFrame(tryFocus);
    }
  }, [collapsedCategories, setKbFocusedCell]);

  const getStudentIdAtRow = useCallback(
    (rowIndex) => (rowIndex >= 0 ? (tableRows[rowIndex]?.id ?? null) : null),
    [tableRows],
  );

  const getStudentIdAtRowRef = useRef(getStudentIdAtRow);
  getStudentIdAtRowRef.current = getStudentIdAtRow;

  const focusCellIfFocusLost = useCallback((rowIndex, columnId) => {
    const cellElement = document.querySelector(
      `[data-row-index="${rowIndex}"][data-column-id="${columnId}"]`,
    );
    if (!(cellElement instanceof HTMLElement)) return;

    const gradebookGrid = document.querySelector(
      '[role="grid"][aria-label="Student grades"]',
    );
    const scrollContainer = gradebookGrid?.closest('[role="region"]');
    const isFocusInGradebook =
      scrollContainer?.contains(document.activeElement) &&
      document.activeElement !== scrollContainer;

    if (!isFocusInGradebook) {
      cellElement.focus();
    }
  }, []);

  useEffect(() => {
    if (kbFocusedCell) {
      setFocusedCell(kbFocusedCell);
      setFocusedStudentId(getStudentIdAtRowRef.current(kbFocusedCell.rowIndex));
    }
  }, [kbFocusedCell]);

  // Re-locate the focused/selected row by its stable id after a re-sort
  // and restore DOM focus there.
  const focusedColumnId = kbFocusedCell?.columnId ?? null;
  useEffect(() => {
    const newRowIndex = findRowIndexById(tableRows, focusedStudentId);
    if (newRowIndex === null) return;

    if (newRowIndex === -1) {
      // Student paged/filtered away - clear rather than leaving focus on
      // whatever row now occupies the old position.
      setFocusedStudentId(null);
      setFocusedCell(null);
      setKbFocusedCell(null);
      return;
    }

    setFocusedCell((prev) =>
      prev && prev.rowIndex !== newRowIndex
        ? { ...prev, rowIndex: newRowIndex }
        : prev,
    );
    setKbFocusedCell((prev) =>
      prev && prev.rowIndex !== newRowIndex
        ? { ...prev, rowIndex: newRowIndex }
        : prev,
    );

    if (!focusedColumnId) return;

    requestAnimationFrame(() => {
      focusCellIfFocusLost(newRowIndex, focusedColumnId);
    });
  }, [
    tableRows,
    focusedStudentId,
    focusedColumnId,
    setKbFocusedCell,
    focusCellIfFocusLost,
  ]);

  const ensureCellVisible = useCallback((cellElement) => {
    const scrollContainer = cellElement.closest(`.${styles.scrollContainer}`);
    if (!scrollContainer) return;

    const cellRect = cellElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const visibleLeft =
      containerRect.left +
      (cellElement.dataset.columnId === 'name' ? 0 : STICKY_COLUMN_WIDTH);
    const visibleTop = containerRect.top + STICKY_HEADER_HEIGHT;
    const visibleRight = containerRect.right - SCROLL_PADDING;
    const visibleBottom = containerRect.bottom - SCROLL_PADDING;

    const isFullyVisible =
      cellRect.left >= visibleLeft + SCROLL_PADDING &&
      cellRect.right <= visibleRight &&
      cellRect.top >= visibleTop + SCROLL_PADDING &&
      cellRect.bottom <= visibleBottom;

    if (!isFullyVisible) {
      let scrollLeft = scrollContainer.scrollLeft;
      let scrollTop = scrollContainer.scrollTop;

      if (cellRect.left < visibleLeft + SCROLL_PADDING) {
        scrollLeft -= visibleLeft + SCROLL_PADDING - cellRect.left;
      } else if (cellRect.right > visibleRight) {
        scrollLeft += cellRect.right - visibleRight + SCROLL_PADDING;
      }

      if (cellRect.top < visibleTop + SCROLL_PADDING) {
        scrollTop -= visibleTop + SCROLL_PADDING - cellRect.top;
      } else if (cellRect.bottom > visibleBottom) {
        scrollTop += cellRect.bottom - visibleBottom + SCROLL_PADDING;
      }

      scrollContainer.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleCellClick = useCallback(
    (rowIndex, columnId) => {
      const selection = window.getSelection();
      if (selection && selection.type === 'Range' && !selection.isCollapsed) {
        return;
      }

      setFocusedCell({ rowIndex, columnId });
      setKbFocusedCell({ rowIndex, columnId });
      setFocusedStudentId(getStudentIdAtRow(rowIndex));

      const cellElement = document.querySelector(
        `[data-row-index="${rowIndex}"][data-column-id="${columnId}"]`,
      );
      if (cellElement instanceof HTMLElement) {
        cellElement.focus();
        ensureCellVisible(cellElement);
      }
    },
    [setKbFocusedCell, ensureCellVisible, getStudentIdAtRow],
  );

  // Separate sticky columns from scrollable columns
  // Only the Name column is sticky
  const stickyColumns = useMemo(() => {
    return columns.filter((col) => col.id === 'name');
  }, [columns]);

  const scrollableColumns = useMemo(() => {
    return columns.filter((col) => col.id !== 'name');
  }, [columns]);

  // Create column index map for data-grid-id attributes (for testing)
  // Maps column IDs to their numeric indices in the full columns array
  const columnIndexMap = useMemo(() => {
    const indexMap = new Map();
    columns.forEach((col, index) => indexMap.set(col.id, index));
    return indexMap;
  }, [columns]);

  // Column virtualizer for horizontal scrolling
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: scrollableColumns.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const col = scrollableColumns[index];
      return col.size || COLUMN_WIDTHS.ASSIGNMENT;
    },
    overscan: VIRTUALIZER_OVERSCAN.COLUMNS,
  });

  // Sync local pagination page index with Redux when page changes,
  // and reset scroll position so each new page starts at the top-left.
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: (reduxPage || 1) - 1,
    }));
    rowVirtualizer.scrollToOffset(0);
    columnVirtualizer.scrollToOffset(0);
  }, [reduxPage]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  const totalHeight = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalHeight - virtualRows[virtualRows.length - 1].end
      : 0;

  const paddingLeft = virtualColumns.length > 0 ? virtualColumns[0].start : 0;
  // Calculate padding right for columns after visible virtual columns
  const totalColumnWidth = columnVirtualizer.getTotalSize();
  const paddingRight =
    virtualColumns.length > 0
      ? totalColumnWidth - virtualColumns[virtualColumns.length - 1].end
      : 0;
  // Only include sticky column width when there are sticky columns (when there are students)
  const hasStickyColumns = stickyColumns.length > 0;
  const tableWidth =
    (hasStickyColumns ? STICKY_COLUMN_WIDTH : 0) + totalColumnWidth;

  // Fetch averages for visible assignments (matches V1 behavior)
  const debouncedUpdateAverages = useMemo(
    () =>
      debounce((assignmentIds) => {
        if (updateAssignmentAverages && assignmentIds.length > 0) {
          updateAssignmentAverages(assignmentIds);
        }
      }, AVERAGES_FETCH_DEBOUNCE),
    [updateAssignmentAverages],
  );

  useEffect(() => {
    if (!updateAssignmentAverages || !data.assignments) return;

    // Get visible assignment IDs from virtual columns
    const visibleAssignmentIds = virtualColumns
      .map((vc) => scrollableColumns[vc.index])
      .filter((col) => col && col.meta?.assignmentId)
      .map((col) => col.meta.assignmentId);

    if (visibleAssignmentIds.length === 0) return;

    // Filter out assignments that already have averages
    const existingAverageIds = map(averages || [], 'assignmentId');
    const newAssignmentIds = difference(
      visibleAssignmentIds,
      existingAverageIds,
    );

    if (newAssignmentIds.length > 0) {
      debouncedUpdateAverages(newAssignmentIds);
    }
  }, [
    virtualColumns,
    scrollableColumns,
    averages,
    debouncedUpdateAverages,
    data.assignments,
    updateAssignmentAverages,
  ]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateAverages.cancel();
    };
  }, [debouncedUpdateAverages]);

  // Check for empty states (matches V1 behavior in InstructorGradebookGrid.jsx)
  const hasStudents = data.students && data.students.length > 0;
  const hasAssignments = data.assignments && data.assignments.length > 0;
  const hasActiveFilters = filterTypes && filterTypes.length > 0;

  // Show empty gradebook when no assignments exist (and not searching/refetching/filtering)
  // V1 condition: hasGradebookFetched && !searchPhrase && !isGradebookReFetching && !hasAssignments
  // hasGradebookFetched is implicitly true since we only render after loading completes
  // Also check for active filters - if filtering results in no assignments, don't show the empty prompt
  if (
    !searchPhrase &&
    !isGradebookReFetching &&
    !hasAssignments &&
    !hasActiveFilters
  ) {
    return <EmptyGradebook type="assignments" onTabChange={onTabChange} />;
  }

  // Show no search results when student search yields no students
  // V1 condition: searchPhrase && !hasStudents && searchType === SEARCH_TYPES.STUDENT
  // Only show "no results" for student searches - assignment searches should still render the gradebook
  if (searchPhrase && !hasStudents && searchType === SEARCH_TYPES.STUDENT) {
    return <NoSearchResultsGradebook searchPhrase={searchPhrase} />;
  }

  return (
    <div className={styles.gradebookContainer}>
      <div
        ref={parentRef}
        className={styles.scrollContainer}
        role="region"
        aria-label="Gradebook grid with student grades"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <table
          ref={tableRef}
          className={styles.table}
          style={{ width: tableWidth, minWidth: tableWidth }}
          role="grid"
          aria-label="Student grades"
          aria-rowcount={tableRows.length + 3}
          aria-colcount={columns.length}
        >
          {/* Column definitions for fixed widths */}
          <colgroup>
            {/* Sticky name column - only when there are students */}
            {hasStickyColumns && (
              <col
                style={{
                  width: STICKY_COLUMN_WIDTH,
                  minWidth: STICKY_COLUMN_WIDTH,
                  maxWidth: STICKY_COLUMN_WIDTH,
                }}
              />
            )}
            {/* Padding column for horizontal scroll (left) - always render for stable layout */}
            <col
              style={{
                width: paddingLeft,
                minWidth: paddingLeft,
                maxWidth: paddingLeft,
              }}
            />
            {/* Virtual columns */}
            {virtualColumns.map((virtualColumn) => (
              <col
                key={virtualColumn.index}
                style={{
                  width: virtualColumn.size,
                  minWidth: virtualColumn.size,
                  maxWidth: virtualColumn.size,
                }}
              />
            ))}
            {/* Padding column for horizontal scroll (right) - for columns after visible ones */}
            <col
              style={{
                width: paddingRight,
                minWidth: paddingRight,
                maxWidth: paddingRight,
              }}
            />
          </colgroup>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <thead
            className={styles.tableHead}
            style={{ position: 'sticky', top: 0 }}
            role="rowgroup"
          >
            {/* Category header row */}
            <CategoryHeader
              categories={data.categories}
              categoryGroups={categoryGroups}
              collapsedCategories={collapsedCategories}
              toggleCategory={toggleCategory}
              stickyColumns={stickyColumns}
              scrollableColumns={scrollableColumns}
              virtualColumns={virtualColumns}
              paddingLeft={paddingLeft}
              paddingRight={paddingRight}
              gradingType={gradingType}
              columnIndexMap={columnIndexMap}
              focusedCell={focusedCell}
              onCellClick={handleCellClick}
            />

            {/* Assignment header row */}
            {table.getHeaderGroups().map((headerGroup) => (
              <AssignmentHeader
                key={headerGroup.id}
                headerGroup={headerGroup}
                stickyColumns={stickyColumns}
                scrollableColumns={scrollableColumns}
                virtualColumns={virtualColumns}
                paddingLeft={paddingLeft}
                paddingRight={paddingRight}
                columnIndexMap={columnIndexMap}
                focusedCell={focusedCell}
                onCellClick={handleCellClick}
                getAriaSortValue={getAriaSortValue}
                hasStudents={hasStudents}
              />
            ))}

            {/* Average row */}
            <AverageRow
              stickyColumns={stickyColumns}
              scrollableColumns={scrollableColumns}
              virtualColumns={virtualColumns}
              paddingLeft={paddingLeft}
              paddingRight={paddingRight}
              averageRow={averageRow}
              focusedCell={focusedCell}
              handleCellClick={handleCellClick}
              formatGrade={formatGrade}
              showPercent={showPercent}
              roundPercentType={roundPercentType}
              settings={rdxGradebookSettings}
              columnIndexMap={columnIndexMap}
              hasStudents={hasStudents}
            />
          </thead>

          <TableBody
            virtualRows={virtualRows}
            virtualColumns={virtualColumns}
            tableRows={tableRows}
            stickyColumns={stickyColumns}
            scrollableColumns={scrollableColumns}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            focusedCell={focusedCell}
            handleCellClick={handleCellClick}
            columnIndexMap={columnIndexMap}
          />
        </table>
      </div>
    </div>
  );
}

export default Gradebook;
